/**
 * AI Assist enablement — runtime checks for non-agent LLM capabilities.
 *
 * Workspace agents use agent_settings (DB). Assists use ai_assist_settings (DB),
 * hydrated into an in-memory cache by aiAssistSettingsStore on workspace load.
 *
 * Canon: DOCS_V4/00-canon/24-ai-capability-taxonomy-and-registry.v1.md
 */

import {
  AI_CAPABILITIES,
  type AiCapabilityMeta,
} from './aiCapabilityRegistry';
import { getCurrentWorkspaceId } from '../supabaseSync';
import { hasEntitlement, type WorkspacePlanRecord } from '../entitlements';

// Re-export so any import sites pointing to this module keep working.
export { AI_ASSIST_TIERS, AI_ASSIST_TIER_SECTIONS } from './aiCapabilityRegistry';

export type AssistSettingsMap = Record<string, boolean>;

let assistCacheWorkspaceId: string | null = null;
let assistCache: AssistSettingsMap = {};

let workspacePlanCache: WorkspacePlanRecord | null = null;

/** Called by aiAssistSettingsStore after DB fetch or save. */
export function hydrateAssistSettingsCache(
  workspaceId: string,
  settings: AssistSettingsMap,
): void {
  assistCacheWorkspaceId = workspaceId;
  assistCache = { ...settings };
}

/** Called when workspace plan is resolved (tenantStore). */
export function hydrateWorkspacePlanCache(plan: WorkspacePlanRecord | null): void {
  workspacePlanCache = plan;
}

export function clearAssistSettingsCache(): void {
  assistCacheWorkspaceId = null;
  assistCache = {};
  workspacePlanCache = null;
}

function envAllows(meta: AiCapabilityMeta): boolean {
  if (!meta.envFlag) return true;
  return import.meta.env[meta.envFlag] !== 'false';
}

/** Whether the workspace's plan includes AI features (agents and assists alike). */
export function isWorkspaceEntitledToAi(workspaceId: string | null): boolean {
  const plan = workspacePlanCache;
  if (!plan) return true;
  if (plan.workspaceId !== workspaceId) return true;
  return hasEntitlement(plan, 'ai_features');
}

function resolveAssistEnabled(
  meta: AiCapabilityMeta,
  workspaceId: string | null,
): boolean {
  if (workspaceId && assistCacheWorkspaceId === workspaceId && meta.id in assistCache) {
    return assistCache[meta.id];
  }
  return meta.defaultEnabled;
}

/**
 * Whether a non-agent AI capability is enabled for this workspace.
 * Workspace agents should use agent_settings.enabled instead.
 */
export function isAiCapabilityEnabled(
  capabilityId: string,
  workspaceId?: string | null,
): boolean {
  const meta = AI_CAPABILITIES[capabilityId];
  if (!meta) return false;
  if (meta.invocationTier === 'workspace_agent') return true;
  if (!envAllows(meta)) return false;

  const wsId = workspaceId ?? getCurrentWorkspaceId();
  if (!isWorkspaceEntitledToAi(wsId)) return false;

  return resolveAssistEnabled(meta, wsId);
}


/** @deprecated Use aiAssistSettingsStore.saveEnabled — kept for tests. */
export function saveAssistEnabled(
  workspaceId: string,
  capabilityId: string,
  enabled: boolean,
): void {
  const settings = { ...assistCache, [capabilityId]: enabled };
  hydrateAssistSettingsCache(workspaceId, settings);
}

/** @deprecated Use aiAssistSettingsStore.getSettings */
export function loadAssistSettings(workspaceId: string): AssistSettingsMap {
  if (assistCacheWorkspaceId === workspaceId) return { ...assistCache };
  return {};
}
